import { type SupplierJoinRequest } from '@choco-admin/types';

import {
  type JoinRequestAllowedUpdateStatus,
  type SuppliersPageGetRequestsToJoinQuery,
  type SuppliersPageUpdateRequestToJoinMutation,
} from '../../../generated';

type RequestsToJoinResponse =
  SuppliersPageGetRequestsToJoinQuery['getRequestsToJoin'];

type UpdateRequestToJoinResponse =
  SuppliersPageUpdateRequestToJoinMutation['updateRequestToJoin'];

/**
 * Copy shown when the backend refuses a read or a write but gives us nothing
 * usable to show. Every message here has to tell a CS reader what to do next.
 */
const GENERIC_LOAD_ERROR =
  'Could not load the requests to join. Refresh the page to try again.';

const GENERIC_UPDATE_ERROR =
  'The request was not updated. Refresh the page and try again.';

/**
 * Reads the successful arm of the `getRequestsToJoin` union.
 *
 * Any failure arm (or a missing response) yields an empty list — the caller
 * pairs this with `getJoinRequestsLoadError` to tell "no requests" apart from
 * "could not load requests".
 *
 * Requests of every status come back. The status filter is deliberately not
 * asked of the backend: it is a DynamoDB `FilterExpression` applied after a
 * hardcoded `Limit: 20`, so a distributor with twenty decided requests returns
 * an empty page and the screen would say nobody is waiting. Reading the same
 * twenty unfiltered and selecting here returns exactly the same set, and it
 * also makes a full window visible (see `countJoinRequestsInWindow`).
 */
export const mapSupplierJoinRequests = (
  response: RequestsToJoinResponse | undefined | null
): SupplierJoinRequest[] => {
  if (response?.__typename !== 'RequestsToJoinConnection') {
    return [];
  }

  return response.edges
    .filter((edge) => edge.node.status === 'pending')
    .map((edge) => ({
      id: edge.node.id,
      user: {
        id: edge.node.user.id,
        name: edge.node.user.name,
        email: edge.node.user.email,
        phone: edge.node.user.phone,
      },
    }));
};

/**
 * How many requests the backend returned, before the pending selection.
 *
 * This is what says whether the twenty-item window is full. The number of
 * pending requests cannot: zero pending is equally consistent with "nobody is
 * waiting" and "the window is full of decided requests and a pending one sits
 * behind it".
 */
export const countJoinRequestsInWindow = (
  response: RequestsToJoinResponse | undefined | null
): number =>
  response?.__typename === 'RequestsToJoinConnection'
    ? response.edges.length
    : 0;

/**
 * Returns CS-facing copy when the read failed, or null when it succeeded.
 *
 * The failure arms carry a backend `message`, but those strings are written for
 * engineers, so they are deliberately not surfaced — the reader gets an action
 * instead.
 */
export const getJoinRequestsLoadError = (
  response: RequestsToJoinResponse | undefined | null,
  hasTransportError = false
): string | null => {
  if (response?.__typename === 'RequestsToJoinConnection') {
    return null;
  }

  if (response?.__typename === 'AuthorizationFailure') {
    return 'You do not have access to the requests to join for this distributor.';
  }

  if (response?.__typename === 'InputValidationFailure') {
    return GENERIC_LOAD_ERROR;
  }

  // No union arm at all: either the query errored at the transport level or it
  // has not resolved yet. Only the former is an error worth showing.
  return hasTransportError ? GENERIC_LOAD_ERROR : null;
};

export type JoinRequestUpdateOutcome =
  | { ok: true; status: JoinRequestAllowedUpdateStatus }
  | {
      ok: false;
      message: string;
      /**
       * Whether the list on screen is known or suspected to be out of date.
       *
       * A refused write is not always a no-op for the reader: "no longer
       * waiting" and "no longer exists" both mean the row they are looking at
       * is stale, and leaving its buttons live invites a second attempt at
       * something that cannot succeed.
       */
      staleView: boolean;
    };

/**
 * Decides whether an approve/deny actually happened.
 *
 * The Apollo client sets `mutate.errorPolicy: 'all'`, so a failed mutation
 * RESOLVES with `errors` populated instead of throwing. A try/catch alone would
 * report success for a write that never landed, so `errors` is checked first
 * and the response union is narrowed second.
 */
export const resolveJoinRequestUpdate = (result: {
  data?: SuppliersPageUpdateRequestToJoinMutation | null;
  errors?: ReadonlyArray<{ message: string }> | null;
}): JoinRequestUpdateOutcome => {
  if (result.errors && result.errors.length > 0) {
    // The write may or may not have landed. Reconcile rather than guess.
    return { ok: false, message: GENERIC_UPDATE_ERROR, staleView: true };
  }

  const response: UpdateRequestToJoinResponse | undefined =
    result.data?.updateRequestToJoin;

  if (response?.__typename === 'UpdateJoinRequestSuccess') {
    return { ok: true, status: response.status };
  }

  if (response?.__typename === 'AuthorizationFailure') {
    return {
      ok: false,
      message: 'You do not have access to decide on this request.',
      staleView: false,
    };
  }

  if (response?.__typename === 'UpdateJoinRequestError') {
    // `notAllowedJoinRequestUpdate` only means the request is no longer pending.
    // It does NOT prove somebody else decided it, and saying so would be wrong
    // in the case that matters most.
    //
    // The backend writes the new status before it links the user
    // (userService ReviewUserRequestToJoinService.review updates the row, then
    // calls handleApprovedRequestToJoin, which can throw). A link that fails
    // therefore leaves the request marked approved with nobody granted access,
    // and every retry is refused by the not-pending guard. From here those two
    // situations are indistinguishable, so the copy sends the reader to check
    // the outcome rather than asserting a cause. It stops there: the portal has
    // no way to add the member by hand, and there is no verified escalation
    // route to name.
    //
    // "above" tracks the card order in TeamTab: Team Members sits first, since
    // that is what the tab is for. Move either card and this word is wrong.
    if (response.errorCode === 'notAllowedJoinRequestUpdate') {
      return {
        ok: false,
        message:
          'This request is no longer waiting for a decision. Check the Team Members list above: if the person is not there, the approval did not finish and cannot be retried here.',
        staleView: true,
      };
    }

    if (response.errorCode === 'notFoundJoinRequest') {
      return {
        ok: false,
        message: 'This request no longer exists.',
        staleView: true,
      };
    }

    return { ok: false, message: GENERIC_UPDATE_ERROR, staleView: true };
  }

  return { ok: false, message: GENERIC_UPDATE_ERROR, staleView: true };
};

/**
 * Success copy for a decision that landed. Kept next to the outcome logic so
 * the wording stays in step with the statuses the backend accepts.
 */
export const describeJoinRequestOutcome = (
  status: JoinRequestAllowedUpdateStatus,
  userName: string
): { title: string; message: string } =>
  status === 'approved'
    ? {
        title: 'Request approved',
        // The backend links the user to the supplier and defaults their role to
        // sales rep (userService ReviewUserRequestToJoinService
        // .linkUserToSupplierEntity), so the confirmation says which access was
        // granted rather than just that something happened.
        message: `${userName} was added to this distributor as a sales rep.`,
      }
    : {
        title: 'Request declined',
        message: `${userName} was not added to this distributor's team.`,
      };
